import type { glassVariants } from './GlassTheme';

/**
 * Fantasy theme - ornate/storybook, parchment + jewel tones. Proof-of-concept
 * second theme variant (see ThemeMaster.tsx for the switching architecture).
 *
 * Structural parity with glassVariants is enforced by this type annotation -
 * TypeScript rejects the object below if any top-level key, sub-key, or
 * light/dark pair glassVariants has is missing here. getThemeClass() fails
 * soft (returns '' on a genuinely missing key, never throws), so without
 * this the gap would be a silent, easy-to-miss unstyled element instead of
 * a build error.
 *
 * glassVariants is declared `as const`, so `typeof glassVariants` infers
 * every leaf as its exact string literal (e.g. the literal type
 * "bg-white/40 ...", not `string`) - assigning against that type directly
 * would require fantasyVariants' values to be IDENTICAL to glass's, not
 * just present. Widen<> recursively replaces every literal string with
 * `string` while keeping the object/array shape (and requiredness of every
 * key) intact, which is the actual property this needs to enforce.
 */
type Widen<T> = T extends string
	? string
	: T extends readonly (infer U)[]
		? readonly Widen<U>[]
		: T extends object
			? { [K in keyof T]: Widen<T[K]> }
			: T;

type GlassVariantsShape = Widen<typeof glassVariants>;

// Palette reference (not literal tokens - just what every color below draws from):
// Parchment: #f4ecd8 / #ecdfc4 / #e0cfa8 (light bg)   Ink-brown: #1f1815 / #241b16 / #2b2118 (dark bg)
// Ink text: #2b1d0e / #3d2914 (light)                 Parchment text: #e8dcc0 / #f4ecd8 (dark)
// Gold/gilt: #b8860b / #d4af37       Amethyst: #6d28d9 / #a78bfa      Sapphire: #1e3a8a / #60a5fa
// Emerald: #14532d / #4ade80         Garnet (errors): #7f1d1d / #f87171

export const fantasyVariants: GlassVariantsShape = {
	background: {
		light: 'bg-gradient-to-br from-[#f4ecd8] via-[#ecdfc4] to-[#e0cfa8] backdrop-blur-md relative before:content-[""] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(184,134,11,0.10),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(109,40,217,0.08),transparent_50%)] before:pointer-events-none',
		dark: 'bg-gradient-to-br from-[#1f1815] via-[#241b16] to-[#2b2118] backdrop-blur-sm relative before:content-[""] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.08),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(167,139,250,0.08),transparent_50%)] before:pointer-events-none',
	},

	backgroundFixed: {
		light: 'bg-gradient-to-br from-[#f4ecd8]/10 via-[#ecdfc4]/10 to-[#e0cfa8]/10 backdrop-blur-sm fixed inset-0 z-0 overflow-hidden pointer-events-none before:content-[""] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(184,134,11,0.02),transparent_50%)] before:pointer-events-none',
		dark: 'bg-gradient-to-br from-[#1f1815]/10 via-[#241b16]/10 to-[#2b2118]/10 backdrop-blur-sm fixed inset-0 z-0 overflow-hidden pointer-events-none before:content-[""] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.02),transparent_50%)] before:pointer-events-none',
	},

	layoutShellHome: {
		light: 'bg-transparent',
		dark: 'bg-transparent',
	},

	card: {
		light: 'backdrop-blur-xl bg-[#faf3e3]/90 shadow-2xl shadow-[#8b6914]/20 border-2 border-[#b8860b]/40 rounded-2xl relative before:content-[""] before:absolute before:inset-0 before:bg-gradient-to-br before:from-[#d4af37]/10 before:via-transparent before:to-transparent before:pointer-events-none before:rounded-[inherit] after:content-[""] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,transparent_0%,rgba(212,175,55,0.06)_50%,transparent_100%)] after:pointer-events-none after:rounded-[inherit]',
		dark: 'backdrop-blur-xl bg-[#2b2118]/90 shadow-2xl shadow-black/50 border-2 border-[#8b6914]/40 rounded-2xl relative before:content-[""] before:absolute before:inset-0 before:bg-gradient-to-br before:from-[#d4af37]/8 before:via-transparent before:to-transparent before:pointer-events-none before:rounded-[inherit] after:content-[""] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,transparent_0%,rgba(212,175,55,0.04)_50%,transparent_100%)] after:pointer-events-none after:rounded-[inherit]',
	},

	pageWrapper: {
		light: 'relative min-h-[calc(100vh-200px)] rounded-2xl backdrop-blur-2xl bg-[#f4ecd8]/20 shadow-2xl border border-[#b8860b]/25 before:content-[""] before:absolute before:inset-0 before:bg-gradient-to-br before:from-[#d4af37]/10 before:via-transparent before:to-transparent before:pointer-events-none before:rounded-[inherit] after:content-[""] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,transparent_0%,rgba(212,175,55,0.05)_50%,transparent_100%)] after:pointer-events-none after:rounded-[inherit]',
		dark: 'relative min-h-[calc(100vh-200px)] rounded-2xl backdrop-blur-2xl bg-[#1f1815]/20 shadow-2xl border border-[#8b6914]/25 before:content-[""] before:absolute before:inset-0 before:bg-gradient-to-br before:from-[#d4af37]/6 before:via-transparent before:to-transparent before:pointer-events-none before:rounded-[inherit] after:content-[""] after:absolute after:inset-0 after:bg-[linear-gradient(135deg,transparent_0%,rgba(212,175,55,0.03)_50%,transparent_100%)] after:pointer-events-none after:rounded-[inherit]',
	},

	header: {
		light: 'backdrop-blur-xl bg-[#f4ecd8]/70 shadow-lg shadow-[#8b6914]/10 border-b border-[#b8860b]/25',
		dark: 'backdrop-blur-xl bg-[#1f1815]/85 shadow-lg shadow-black/25 border-b border-[#8b6914]/30',
	},

	footer: {
		light: 'backdrop-blur-md bg-[#f4ecd8]/40 shadow-lg shadow-[#8b6914]/6 border-t border-[#b8860b]/15',
		dark: 'backdrop-blur-md bg-[#1f1815]/80 shadow-lg shadow-black/15 border-t border-[#8b6914]/20',
	},

	input: {
		light: 'bg-[#faf3e3] border-2 border-[#8b6914]/50 shadow-sm focus:border-[#8b6914] focus:shadow-md',
		dark: 'bg-[#241b16] border-2 border-[#8b6914]/45 shadow-sm focus:border-[#d4af37] focus:shadow-md',
		error: {
			light: 'ring-2 ring-[#7f1d1d]/60 bg-[#fdecec]/60 shadow-lg shadow-[#7f1d1d]/10',
			dark: 'ring-2 ring-[#f87171]/60 bg-[#3a1414]/50 shadow-lg shadow-[#f87171]/10',
		},
	},

	// Matte, carved silhouette (no backdrop-blur) with a double-border "gilded
	// frame" and a small corner sigil on primary — deliberately distinct from
	// Glass's soft frosted look, not just a recolor of it.
	button: {
		primary: {
			light:
				'relative bg-gradient-to-b from-[#c99820] to-[#8b6914] hover:from-[#d4a52c] hover:to-[#96731a] ' +
				'text-[#faf3e3] font-semibold shadow-[0_2px_0_0_#6b4f0f,0_4px_10px_-2px_rgba(139,105,20,0.5)] ' +
				'hover:shadow-[0_2px_0_0_#6b4f0f,0_6px_14px_-2px_rgba(139,105,20,0.6)] ' +
				'active:shadow-[0_0px_0_0_#6b4f0f] active:translate-y-[2px] ' +
				'ring-1 ring-inset ring-[#f4e4b8]/60 border border-[#5e4610] ' +
				"after:content-['✦'] after:absolute after:-top-2 after:-right-2 after:text-[11px] after:text-[#f4e4b8] after:drop-shadow-sm " +
				'transition-all duration-200',
			dark:
				'relative bg-gradient-to-b from-[#c99820] to-[#8b6914] hover:from-[#d4a52c] hover:to-[#96731a] ' +
				'text-[#f4ecd8] font-semibold shadow-[0_2px_0_0_#4a3610,0_4px_10px_-2px_rgba(0,0,0,0.5)] ' +
				'hover:shadow-[0_2px_0_0_#4a3610,0_6px_14px_-2px_rgba(0,0,0,0.6)] ' +
				'active:shadow-[0_0px_0_0_#4a3610] active:translate-y-[2px] ' +
				'ring-1 ring-inset ring-[#f4e4b8]/40 border border-[#4a3610] ' +
				"after:content-['✦'] after:absolute after:-top-2 after:-right-2 after:text-[11px] after:text-[#f4e4b8] after:drop-shadow-sm " +
				'transition-all duration-200',
		},
		secondary: {
			light:
				'bg-[#f4ecd8] hover:bg-[#ecdfc4] text-[#3d2914] font-semibold shadow-sm ' +
				'border-2 border-[#8b6914]/70 hover:border-[#8b6914] transition-all duration-200',
			dark:
				'bg-[#241b16] hover:bg-[#2b2118] text-[#f4ecd8] font-semibold shadow-sm ' +
				'border-2 border-[#8b6914]/60 hover:border-[#8b6914] transition-all duration-200',
		},
		ghost: {
			light:
				'bg-transparent text-[#3d2914] hover:text-[#2b1d0e] shadow-none ' +
				'border-b-2 border-transparent hover:border-[#b8860b]/70 rounded-none transition-all duration-200',
			dark:
				'bg-transparent text-[#e8dcc0] hover:text-[#f4ecd8] shadow-none ' +
				'border-b-2 border-transparent hover:border-[#8b6914]/70 rounded-none transition-all duration-200',
		},
		disabled: {
			light: 'bg-[#d9cba8]/60 cursor-not-allowed opacity-50 font-semibold shadow-sm text-[#8b7355] border border-[#b8a373]/30',
			dark: 'bg-[#3a2f24]/60 cursor-not-allowed opacity-50 font-semibold shadow-sm text-[#8b7355] border border-[#4a3d2c]/30',
		},
	},

	checkbox: {
		light: 'bg-[#faf3e3] border-2 border-[#8b6914]/60 checked:bg-[#b8860b] checked:border-[#b8860b] shadow-sm',
		dark: 'bg-[#241b16] border-2 border-[#8b6914]/50 checked:bg-[#d4af37] checked:border-[#d4af37] shadow-sm',
	},
	radio: {
		light: 'bg-[#faf3e3] border-2 border-[#8b6914]/60 checked:bg-[#b8860b] checked:border-[#b8860b] shadow-sm',
		dark: 'bg-[#241b16] border-2 border-[#8b6914]/50 checked:bg-[#d4af37] checked:border-[#d4af37] shadow-sm',
	},
	toggle: {
		light: 'bg-[#d9cba8] checked:bg-[#b8860b] shadow-sm border-2 border-[#8b6914]/40',
		dark: 'bg-[#3a2f24] checked:bg-[#d4af37] shadow-sm border-2 border-[#8b6914]/40',
	},
	select: {
		light: 'bg-[#faf3e3] border-2 border-[#8b6914]/50 shadow-sm focus:border-[#8b6914] focus:shadow-md',
		dark: 'bg-[#241b16] border-2 border-[#8b6914]/45 shadow-sm focus:border-[#d4af37] focus:shadow-md',
	},
	textarea: {
		light: 'bg-[#faf3e3] border-2 border-[#8b6914]/50 shadow-sm focus:border-[#8b6914] focus:shadow-md resize-none',
		dark: 'bg-[#241b16] border-2 border-[#8b6914]/45 shadow-sm focus:border-[#d4af37] focus:shadow-md resize-none',
	},
	section: {
		light: 'backdrop-blur-lg bg-[#f4ecd8]/40 shadow-lg shadow-[#8b6914]/6 rounded-xl border border-[#b8860b]/25',
		dark: 'backdrop-blur-lg bg-[#2b2118]/30 shadow-lg shadow-black/20 rounded-xl border border-[#8b6914]/20',
	},
	container: {
		light: 'backdrop-blur-xl bg-[#f4ecd8]/50 shadow-xl shadow-[#8b6914]/8 border border-[#b8860b]/30',
		dark: 'backdrop-blur-xl bg-[#2b2118]/40 shadow-xl shadow-black/25 border border-[#8b6914]/25',
	},
	panel: {
		light: 'backdrop-blur-xl bg-[#f4ecd8]/60 shadow-2xl shadow-[#8b6914]/10 border border-[#b8860b]/35',
		dark: 'backdrop-blur-xl bg-[#2b2118]/50 shadow-2xl shadow-black/30 border border-[#8b6914]/30',
	},
	slider: {
		light: 'backdrop-blur-sm bg-[#faf3e3]/90 shadow-lg focus:bg-[#faf3e3] focus:shadow-xl focus:shadow-[#b8860b]/15',
		dark: 'backdrop-blur-sm bg-[#241b16]/90 shadow-lg focus:bg-[#241b16] focus:shadow-xl focus:shadow-[#d4af37]/10',
	},
	rangeSlider: {
		light:
			'w-full h-2.5 rounded-full appearance-none cursor-pointer touch-none bg-transparent ' +
			'focus:outline-none focus:ring-2 focus:ring-[#b8860b]/35 focus:ring-offset-2 focus:ring-offset-[#f4ecd8]/80 ' +
			'accent-[#8b6914] [&:hover]:accent-[#b8860b] [&:active]:accent-[#6b4f0f] ' +
			'[&::-webkit-slider-runnable-track]:h-2.5 [&::-webkit-slider-runnable-track]:rounded-full ' +
			'[&::-webkit-slider-runnable-track]:backdrop-blur-sm [&::-webkit-slider-runnable-track]:bg-[#faf3e3]/55 ' +
			'[&::-webkit-slider-runnable-track]:border [&::-webkit-slider-runnable-track]:border-[#b8860b]/35 ' +
			'[&::-webkit-slider-runnable-track]:shadow-[inset_0_1px_3px_rgba(139,105,20,0.10)] ' +
			'[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full ' +
			'[&::-webkit-slider-thumb]:bg-gradient-to-br [&::-webkit-slider-thumb]:from-[#b8860b] [&::-webkit-slider-thumb]:to-[#8b6914] ' +
			'[&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-[#8b6914]/30 [&::-webkit-slider-thumb]:ring-2 [&::-webkit-slider-thumb]:ring-[#faf3e3]/70 [&::-webkit-slider-thumb]:border-0 ' +
			'[&::-moz-range-track]:h-2.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:backdrop-blur-sm [&::-moz-range-track]:bg-[#faf3e3]/55 ' +
			'[&::-moz-range-track]:border [&::-moz-range-track]:border-[#b8860b]/35 [&::-moz-range-track]:shadow-[inset_0_1px_3px_rgba(139,105,20,0.10)] ' +
			'[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full ' +
			'[&::-moz-range-thumb]:bg-gradient-to-br [&::-moz-range-thumb]:from-[#b8860b] [&::-moz-range-thumb]:to-[#8b6914] ' +
			'[&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:shadow-[#8b6914]/30 [&::-moz-range-thumb]:ring-2 [&::-moz-range-thumb]:ring-[#faf3e3]/70 [&::-moz-range-thumb]:border-0',
		dark:
			'w-full h-2.5 rounded-full appearance-none cursor-pointer touch-none bg-transparent ' +
			'focus:outline-none focus:ring-2 focus:ring-[#d4af37]/40 focus:ring-offset-2 focus:ring-offset-[#1f1815]/80 ' +
			'accent-[#d4af37] [&:hover]:accent-[#e8c458] [&:active]:accent-[#f0d478] ' +
			'[&::-webkit-slider-runnable-track]:h-2.5 [&::-webkit-slider-runnable-track]:rounded-full ' +
			'[&::-webkit-slider-runnable-track]:backdrop-blur-sm [&::-webkit-slider-runnable-track]:bg-[#241b16]/65 ' +
			'[&::-webkit-slider-runnable-track]:border [&::-webkit-slider-runnable-track]:border-[#8b6914]/40 ' +
			'[&::-webkit-slider-runnable-track]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.25)] ' +
			'[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full ' +
			'[&::-webkit-slider-thumb]:bg-gradient-to-br [&::-webkit-slider-thumb]:from-[#d4af37] [&::-webkit-slider-thumb]:to-[#b8860b] ' +
			'[&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-[#d4af37]/25 [&::-webkit-slider-thumb]:ring-2 [&::-webkit-slider-thumb]:ring-[#f4ecd8]/15 [&::-webkit-slider-thumb]:border-0 ' +
			'[&::-moz-range-track]:h-2.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:backdrop-blur-sm [&::-moz-range-track]:bg-[#241b16]/65 ' +
			'[&::-moz-range-track]:border [&::-moz-range-track]:border-[#8b6914]/40 [&::-moz-range-track]:shadow-[inset_0_1px_3px_rgba(0,0,0,0.25)] ' +
			'[&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full ' +
			'[&::-moz-range-thumb]:bg-gradient-to-br [&::-moz-range-thumb]:from-[#d4af37] [&::-moz-range-thumb]:to-[#b8860b] ' +
			'[&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:shadow-[#d4af37]/25 [&::-moz-range-thumb]:ring-2 [&::-moz-range-thumb]:ring-[#f4ecd8]/15 [&::-moz-range-thumb]:border-0',
	},
	progressBar: {
		track: {
			light: 'backdrop-blur-sm bg-[#e0cfa8]/60 shadow-inner',
			dark: 'backdrop-blur-sm bg-[#3a2f24]/60 shadow-inner',
		},
		fill: {
			light: 'bg-[#b8860b] shadow-lg shadow-[#b8860b]/25',
			dark: 'bg-[#d4af37] shadow-lg shadow-[#d4af37]/25',
		},
	},
	skeleton: {
		light: 'backdrop-blur-sm bg-[#e0cfa8]/60 animate-pulse',
		dark: 'backdrop-blur-sm bg-[#3a2f24]/60 animate-pulse',
	},
	spinner: {
		light: 'border-[#e0cfa8]/60 border-t-[#b8860b]',
		dark: 'border-[#3a2f24]/60 border-t-[#d4af37]',
	},

	loadingSpinner: {
		small: {
			light: 'w-4 h-4 border-2 border-[#faf3e3]/30 border-t-[#faf3e3] rounded-full animate-spin',
			dark: 'w-4 h-4 border-2 border-[#e8dcc0]/30 border-t-[#e8dcc0] rounded-full animate-spin',
		},
		medium: {
			light: 'w-8 h-8 border-b-2 border-[#b8860b] rounded-full animate-spin',
			dark: 'w-8 h-8 border-b-2 border-[#d4af37] rounded-full animate-spin',
		},
		large: {
			light: 'w-12 h-12 border-4 border-[#e0cfa8] border-t-[#8b6914] rounded-full animate-spin',
			dark: 'w-12 h-12 border-4 border-[#4a3d2c] border-t-[#d4af37] rounded-full animate-spin',
		},
	},

	warningAlert: {
		light: 'backdrop-blur-md bg-[#fdf3e0]/90 shadow-lg shadow-[#b8860b]/10 text-[#7a5a0a] border border-[#d4af37]/40',
		dark: 'backdrop-blur-md bg-[#3a2f10]/60 shadow-lg shadow-[#d4af37]/10 text-[#e8c458] border border-[#8b6914]/40',
	},

	warningText: {
		light: 'text-[#8b6914]',
		dark: 'text-[#e8c458]',
	},

	modalBackdrop: {
		light: 'fixed inset-0 backdrop-blur-sm bg-[#2b1d0e]/30 z-50',
		dark: 'fixed inset-0 backdrop-blur-sm bg-black/50 z-50',
	},

	errorBoundary: {
		container: {
			light: 'min-h-screen flex items-center justify-center p-4',
			dark: 'min-h-screen flex items-center justify-center p-4',
		},
		card: {
			light: 'backdrop-blur-xl bg-[#faf3e3]/95 shadow-2xl rounded-2xl p-8 max-w-lg w-full border border-[#7f1d1d]/40',
			dark: 'backdrop-blur-xl bg-[#241b16]/95 shadow-2xl rounded-2xl p-8 max-w-lg w-full border border-[#f87171]/40',
		},
		background: {
			light: 'bg-[#fdecec]/70',
			dark: 'bg-[#3a1414]/30',
		},
	},

	imageBorder: {
		light: 'border border-[#b8860b]/35 rounded-lg',
		dark: 'border border-[#8b6914]/35 rounded-lg',
	},

	media: {
		pendingFrame: {
			light: 'min-h-32 w-full rounded-xl animate-pulse',
			dark: 'min-h-32 w-full rounded-xl animate-pulse',
		},
		loadingBox: {
			light: 'flex min-h-32 w-full items-center justify-center rounded-xl text-sm',
			dark: 'flex min-h-32 w-full items-center justify-center rounded-xl text-sm',
		},
		heroBackdrop: {
			light: 'pointer-events-none fixed inset-0 z-0 h-[100dvh] min-h-[100dvh] w-full overflow-hidden',
			dark: 'pointer-events-none fixed inset-0 z-0 h-[100dvh] min-h-[100dvh] w-full overflow-hidden',
		},
		heroBackdropVideo: {
			light: 'pointer-events-none absolute inset-0 z-0 block h-full w-full min-h-full min-w-full object-cover object-center',
			dark: 'pointer-events-none absolute inset-0 z-0 block h-full w-full min-h-full min-w-full object-cover object-center',
		},
		heroBackdropScrim: {
			light: 'pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-[#f4ecd8]/60 via-[#f4ecd8]/72 to-[#f4ecd8]/60',
			dark: 'pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-[#1f1815]/40 via-[#1f1815]/55 to-[#1f1815]/40',
		},
		heroOrbLayer: {
			light: 'fixed inset-0 overflow-hidden pointer-events-none z-[1]',
			dark: 'fixed inset-0 overflow-hidden pointer-events-none z-[1]',
		},
		heroContentLayer: {
			light: 'relative z-10',
			dark: 'relative z-10',
		},
	},

	placeholderText: {
		light: 'placeholder:text-[#8b7355]',
		dark: 'placeholder:text-[#6b5d4a]',
	},

	themeToggleHover: {
		light: 'hover:bg-[#e0cfa8]/50 hover:shadow-md hover:shadow-[#b8860b]/10',
		dark: 'hover:bg-[#2b2118]/50 hover:shadow-md hover:shadow-[#d4af37]/10',
	},

	tooltip: {
		light: 'backdrop-blur-md bg-[#2b1d0e]/92 text-[#f4ecd8] shadow-lg shadow-black/20 text-sm rounded-lg',
		dark: 'backdrop-blur-md bg-[#f4ecd8]/92 text-[#2b1d0e] shadow-lg shadow-black/20 text-sm rounded-lg',
	},
	popover: {
		light: 'backdrop-blur-xl bg-[#faf3e3]/95 shadow-2xl shadow-[#8b6914]/10 border border-[#b8860b]/30 rounded-xl',
		dark: 'backdrop-blur-xl bg-[#241b16]/95 shadow-2xl shadow-black/25 border border-[#8b6914]/35 rounded-xl',
	},
	drawer: {
		light: 'backdrop-blur-xl bg-[#faf3e3]/95 shadow-2xl shadow-[#8b6914]/10',
		dark: 'backdrop-blur-xl bg-[#241b16]/95 shadow-2xl shadow-black/25',
	},

	table: {
		light: 'backdrop-blur-lg bg-[#faf3e3]/85 shadow-xl shadow-[#8b6914]/5 border border-[#b8860b]/20',
		dark: 'backdrop-blur-lg bg-[#241b16]/85 shadow-xl shadow-black/15 border border-[#8b6914]/25',
	},
	tableHeader: {
		light: 'backdrop-blur-md bg-[#e0cfa8]/50 border-b border-[#b8860b]/30',
		dark: 'backdrop-blur-md bg-[#2b2118]/60 border-b border-[#8b6914]/35',
	},
	tableRow: {
		light: 'hover:bg-[#faf3e3]/60 border-b border-[#b8860b]/15 text-[#2b1d0e]',
		dark: 'hover:bg-[#2b2118]/50 border-b border-[#8b6914]/20 text-[#e8dcc0]',
	},
	list: {
		light: 'backdrop-blur-lg bg-[#faf3e3]/85 shadow-lg shadow-[#8b6914]/5 rounded-xl',
		dark: 'backdrop-blur-lg bg-[#241b16]/85 shadow-lg shadow-black/15 rounded-xl',
	},
	listItem: {
		light: 'hover:bg-[#faf3e3]/50 border-b border-[#b8860b]/15 last:border-b-0 text-[#2b1d0e] hover:text-[#2b1d0e]',
		dark: 'hover:bg-[#2b2118]/45 border-b border-[#8b6914]/20 last:border-b-0 text-[#e8dcc0] hover:text-[#e8dcc0]',
	},

	text: {
		primary: {
			light: 'text-[#2b1d0e]',
			dark: 'text-[#e8dcc0]',
		},
		secondary: {
			light: 'text-[#5a4626]',
			dark: 'text-[#c4b596]',
		},
		muted: {
			light: 'text-[#8b7355]',
			dark: 'text-[#8b7d63]',
		},
		label: {
			light: 'text-[#3d2914] font-medium',
			dark: 'text-[#d9cba8] font-medium',
		},
		placeholder: {
			light: 'placeholder-[#8b7355]',
			dark: 'placeholder-[#6b5d4a]',
		},
		heading: {
			light: "font-['Cinzel',serif] tracking-wide text-[#3d2914] font-bold",
			dark: "font-['Cinzel',serif] tracking-wide text-[#e8dcc0] font-bold",
		},
		accent: {
			light: 'text-[#7c3aed] font-semibold',
			dark: 'text-[#a78bfa] font-semibold',
		},
		error: {
			light: 'text-[#991b1b]',
			dark: 'text-[#f87171]',
		},
		errorHeading: {
			light: 'text-[#7f1d1d] font-semibold',
			dark: 'text-[#fca5a5] font-semibold',
		},
		success: {
			light: 'text-[#166534]',
			dark: 'text-[#4ade80]',
		},
		info: {
			light: 'text-[#1e3a8a]',
			dark: 'text-[#60a5fa]',
		},
		infoHeading: {
			light: 'text-[#1e3a8a] font-semibold',
			dark: 'text-[#93c5fd] font-semibold',
		},
		strikethrough: {
			light: 'line-through decoration-[#2b1d0e]/80',
			dark: 'line-through decoration-[#e8dcc0]/80',
		},
		multiline: {
			light: 'whitespace-pre-line',
			dark: 'whitespace-pre-line',
		},
	},

	buttonText: {
		primary: {
			light: "font-['Cinzel',serif] tracking-wide text-[#faf3e3] font-semibold drop-shadow-sm",
			dark: "font-['Cinzel',serif] tracking-wide text-[#f4ecd8] font-semibold drop-shadow-sm",
		},
		secondary: {
			light: "font-['Cinzel',serif] tracking-wide text-[#3d2914] font-semibold drop-shadow-sm",
			dark: "font-['Cinzel',serif] tracking-wide text-[#f4ecd8] font-semibold drop-shadow-sm",
		},
		ghost: {
			light: "font-['Cinzel',serif] tracking-wide text-[#3d2914] hover:text-[#2b1d0e] font-medium drop-shadow-sm",
			dark: "font-['Cinzel',serif] tracking-wide text-[#e8dcc0] hover:text-[#f4ecd8] font-medium drop-shadow-sm",
		},
		disabled: {
			light: 'text-[#8b7355] font-medium',
			dark: 'text-[#6b5d4a] font-medium',
		},
	},

	badge: {
		light: 'bg-[#e0cfa8] text-[#3d2914] border border-[#b8860b]/40',
		dark: 'bg-[#3a2f24] text-[#e8dcc0] border border-[#8b6914]/40',
	},

	dashboardSettings: {
		toggleTrackOn: {
			light: 'bg-gradient-to-r from-[#b8860b] to-[#8b6914] shadow-md hover:shadow-lg',
			dark: 'bg-gradient-to-r from-[#d4af37] to-[#b8860b] shadow-md hover:shadow-lg',
		},
		toggleTrackOff: {
			light: 'bg-[#d9cba8]/80 backdrop-blur-sm',
			dark: 'bg-[#3a2f24]/80 backdrop-blur-sm',
		},
		toggleKnob: {
			light: 'bg-[#faf3e3] shadow-lg',
			dark: 'bg-[#f4ecd8] shadow-lg',
		},
		toggleFocusRing: {
			light: 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#b8860b]/50',
			dark: 'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#d4af37]/50',
		},
		radioCardSelected: {
			light: 'bg-gradient-to-br from-[#faf0d8]/60 to-[#f0e2b8]/40 border-[#b8860b]/50 shadow-lg scale-[1.02] border-2',
			dark: 'bg-gradient-to-br from-[#3a2f10]/40 to-[#2b2118]/30 border-[#d4af37]/50 shadow-lg scale-[1.02] border-2',
		},
		radioCardUnselected: {
			light: 'border-2 border-transparent hover:border-[#b8860b]/30 hover:shadow-md hover:scale-[1.01]',
			dark: 'border-2 border-transparent hover:border-[#8b6914]/30 hover:shadow-md hover:scale-[1.01]',
		},
		radioOuterChecked: {
			light: 'border-[#8b6914] bg-[#8b6914] shadow-md border-2',
			dark: 'border-[#d4af37] bg-[#d4af37] shadow-md border-2',
		},
		radioOuterUnchecked: {
			light: 'border-2 border-[#d9cba8] group-hover:border-[#b8860b]',
			dark: 'border-2 border-[#4a3d2c] group-hover:border-[#8b6914]',
		},
		radioInnerDot: {
			light: 'bg-[#faf3e3] shadow-sm',
			dark: 'bg-[#2b2118] shadow-sm',
		},
		passwordInputFocus: {
			light: 'focus:scale-[1.01] focus:ring-2 focus:ring-[#b8860b]/50',
			dark: 'focus:scale-[1.01] focus:ring-2 focus:ring-[#d4af37]/50',
		},
		passwordMeterTrack: {
			light: 'backdrop-blur-sm bg-[#e0cfa8]/80',
			dark: 'backdrop-blur-sm bg-[#3a2f24]/80',
		},
		passwordMeterWeak: {
			light: 'bg-[#991b1b]',
			dark: 'bg-[#991b1b]',
		},
		passwordMeterMedium: {
			light: 'bg-[#b8860b]',
			dark: 'bg-[#b8860b]',
		},
		passwordMeterStrong: {
			light: 'bg-[#166534]',
			dark: 'bg-[#166534]',
		},
	},

	form: {
		light: 'backdrop-blur-xl bg-[#f4ecd8]/50 shadow-xl rounded-2xl shadow-[#8b6914]/8 border border-[#b8860b]/30',
		dark: 'backdrop-blur-xl bg-[#2b2118]/40 shadow-xl rounded-2xl shadow-black/25 border border-[#8b6914]/25',
	},

	modal: {
		overlay: {
			light: 'backdrop-blur-sm bg-[#2b1d0e]/30',
			dark: 'backdrop-blur-sm bg-black/50',
		},
		content: {
			light: 'backdrop-blur-3xl bg-[#faf3e3]/90 shadow-2xl rounded-2xl shadow-[#8b6914]/15 border-2 border-[#b8860b]/40',
			dark: 'backdrop-blur-xl bg-[#241b16]/85 shadow-2xl rounded-2xl shadow-black/40 border-2 border-[#8b6914]/40',
		},
	},

	alert: {
		info: {
			light: 'backdrop-blur-md bg-[#e8eefc]/90 shadow-lg shadow-[#1e3a8a]/10 text-[#1e3a8a]',
			dark: 'backdrop-blur-md bg-[#1e293b]/60 shadow-lg shadow-[#60a5fa]/10 text-[#93c5fd]',
		},
		success: {
			light: 'backdrop-blur-md bg-[#e8f5e9]/90 shadow-lg shadow-[#166534]/10 text-[#166534]',
			dark: 'backdrop-blur-md bg-[#14321f]/60 shadow-lg shadow-[#4ade80]/10 text-[#86efac]',
		},
		warning: {
			light: 'backdrop-blur-md bg-[#fdf3e0]/90 shadow-lg shadow-[#b8860b]/10 text-[#7a5a0a]',
			dark: 'backdrop-blur-md bg-[#3a2f10]/60 shadow-lg shadow-[#d4af37]/10 text-[#e8c458]',
		},
		error: {
			light: 'backdrop-blur-md bg-[#fdecec]/90 shadow-lg shadow-[#7f1d1d]/10 text-[#7f1d1d]',
			dark: 'backdrop-blur-md bg-[#3a1414]/60 shadow-lg shadow-[#f87171]/10 text-[#fca5a5]',
		},
	},

	orbs: {
		light: ['bg-gradient-to-br from-[#d4af37]/20 to-[#8b6914]/20', 'bg-gradient-to-tr from-[#7c3aed]/15 to-[#1e3a8a]/15'],
		dark: ['bg-gradient-to-br from-[#d4af37]/25 to-[#8b6914]/25', 'bg-gradient-to-tr from-[#a78bfa]/20 to-[#60a5fa]/20'],
	},

	divider: {
		light: 'shadow-sm shadow-[#8b6914]/10 bg-gradient-to-r from-transparent via-[#b8860b]/20 to-transparent h-px',
		dark: 'shadow-sm shadow-black/15 bg-gradient-to-r from-transparent via-[#8b6914]/25 to-transparent h-px',
	},

	dividerText: {
		light: 'bg-[#faf3e3]/95 text-[#5a4626] shadow-sm shadow-[#8b6914]/5',
		dark: 'bg-[#241b16]/95 text-[#c4b596] shadow-sm shadow-black/10',
	},

	sidebar: {
		light: 'backdrop-blur-lg bg-[#f4ecd8]/90 shadow-xl shadow-[#8b6914]/8',
		dark: 'backdrop-blur-lg bg-[#1f1815]/90 shadow-xl shadow-black/25 border border-[#8b6914]/25',
	},

	dropdown: {
		light: 'backdrop-blur-xl bg-[#faf3e3]/95 shadow-2xl rounded-xl shadow-[#8b6914]/12',
		dark: 'backdrop-blur-xl bg-[#241b16]/95 shadow-2xl rounded-xl shadow-black/30',
	},

	avatar: {
		light: 'backdrop-blur-sm bg-[#faf3e3]/70 shadow-lg shadow-[#8b6914]/10 hover:shadow-xl hover:shadow-[#8b6914]/15',
		dark: 'backdrop-blur-sm bg-[#241b16]/70 shadow-lg shadow-black/15 hover:shadow-xl hover:shadow-black/20',
	},

	separator: {
		light: 'shadow-sm shadow-[#8b6914]/5 bg-gradient-to-r from-transparent via-[#b8860b]/30 to-transparent h-px my-2',
		dark: 'shadow-sm shadow-black/10 bg-gradient-to-r from-transparent via-[#8b6914]/30 to-transparent h-px my-2',
	},

	nav: {
		main: {
			light: 'backdrop-blur-lg bg-[#f4ecd8]/50 shadow-xl shadow-[#8b6914]/6 border-b border-[#b8860b]/15',
			dark: 'backdrop-blur-lg bg-[#1f1815]/85 shadow-xl shadow-black/25 border-b border-[#8b6914]/25',
		},
		item: {
			light: 'hover:bg-[#e0cfa8]/30 hover:shadow-md hover:shadow-[#8b6914]/10 transition-all duration-200 text-[#2b1d0e] hover:text-[#2b1d0e] backdrop-blur-sm rounded-lg',
			dark: 'hover:bg-[#2b2118]/40 hover:shadow-md hover:shadow-black/15 transition-all duration-200 text-[#e8dcc0] hover:text-[#e8dcc0] backdrop-blur-sm rounded-lg',
		},
		itemActive: {
			light: 'bg-[#f0e2b8]/50 shadow-md shadow-[#b8860b]/20 text-[#2b1d0e] font-semibold backdrop-blur-lg rounded-lg border border-[#b8860b]/35 transform scale-105',
			dark: 'bg-[#3a2f10]/50 shadow-md shadow-[#d4af37]/20 text-[#e8dcc0] font-semibold backdrop-blur-lg rounded-lg border border-[#8b6914]/40 transform scale-105',
		},
		dropdown: {
			light: 'backdrop-blur-xl bg-[#faf3e3]/95 shadow-2xl shadow-[#8b6914]/12 border border-[#b8860b]/40 rounded-xl',
			dark: 'backdrop-blur-xl bg-[#241b16]/95 shadow-2xl shadow-black/30 border border-[#8b6914]/40 rounded-xl',
		},
		funToolsChevron: {
			light: 'w-4 h-4 shrink-0 transition-transform duration-200',
			dark: 'w-4 h-4 shrink-0 transition-transform duration-200',
		},
		funToolsChevronOpen: {
			light: 'rotate-180',
			dark: 'rotate-180',
		},
	},

	avatarButton: {
		light: 'backdrop-blur-sm bg-[#7c3aed]/10 hover:bg-[#7c3aed]/20 shadow-lg shadow-[#7c3aed]/15 hover:shadow-xl hover:shadow-[#7c3aed]/20',
		dark: 'backdrop-blur-sm bg-[#a78bfa]/15 hover:bg-[#a78bfa]/25 shadow-lg shadow-[#a78bfa]/15 hover:shadow-xl hover:shadow-[#a78bfa]/20',
	},

	filter: {
		light: 'backdrop-blur-sm bg-[#faf3e3]/80 hover:bg-[#faf3e3]/95 text-[#2b1d0e] hover:text-[#2b1d0e] border border-[#b8860b]/30 shadow-sm hover:shadow-md',
		dark: 'backdrop-blur-sm bg-[#241b16]/80 hover:bg-[#241b16]/95 text-[#e8dcc0] hover:text-[#e8dcc0] border border-[#8b6914]/30 shadow-sm hover:shadow-md',
	},
	filterActive: {
		light: 'backdrop-blur-sm bg-[#f0e2b8]/40 text-[#2b1d0e] font-semibold border border-[#b8860b]/40 shadow-md',
		dark: 'backdrop-blur-sm bg-[#3a2f10]/40 text-[#e8dcc0] font-semibold border border-[#8b6914]/40 shadow-md',
	},

	buttonAccent: {
		light: 'text-[#7c3aed] hover:text-[#6d28d9] font-semibold',
		dark: 'text-[#a78bfa] hover:text-[#c4b5fd] font-semibold',
	},

	link: {
		light: 'text-[#7c3aed] hover:text-[#6d28d9] font-medium underline decoration-[#7c3aed]/30 hover:decoration-[#7c3aed]/60',
		dark: 'text-[#a78bfa] hover:text-[#c4b5fd] font-medium underline decoration-[#a78bfa]/30 hover:decoration-[#a78bfa]/60',
	},

	buttonLike: {
		primary: {
			light: 'backdrop-blur-xl bg-[#8b6914]/95 hover:bg-[#6b4f0f] text-[#faf3e3] font-semibold shadow-lg shadow-[#8b6914]/25 hover:shadow-xl hover:shadow-[#8b6914]/30 border border-[#6b4f0f]/50 cursor-pointer transition-all duration-200',
			dark: 'backdrop-blur-xl bg-[#d4af37]/90 hover:bg-[#c99820] text-[#1f1815] font-semibold shadow-lg shadow-black/30 hover:shadow-xl hover:shadow-black/40 border border-[#d4af37]/50 cursor-pointer transition-all duration-200',
		},
		secondary: {
			light: 'backdrop-blur-xl bg-[#e0cfa8]/20 hover:bg-[#e0cfa8]/40 text-[#2b1d0e] font-semibold shadow-lg shadow-[#8b6914]/5 hover:shadow-lg hover:shadow-[#8b6914]/15 border border-[#b8860b]/15 hover:border-[#b8860b]/35 cursor-pointer transition-all duration-200 drop-shadow-sm',
			dark: 'backdrop-blur-xl bg-[#2b2118]/25 hover:bg-[#2b2118]/50 text-[#f4ecd8] font-semibold shadow-lg shadow-black/10 hover:shadow-lg hover:shadow-black/20 border border-[#8b6914]/15 hover:border-[#8b6914]/35 cursor-pointer transition-all duration-200 drop-shadow-sm',
		},
		ghost: {
			light: 'backdrop-blur-xl bg-transparent hover:bg-[#e0cfa8]/25 text-[#3d2914] hover:text-[#2b1d0e] shadow-none hover:shadow-md hover:shadow-[#8b6914]/10 border border-transparent hover:border-[#b8860b]/25 cursor-pointer transition-all duration-200 font-medium drop-shadow-sm',
			dark: 'backdrop-blur-xl bg-transparent hover:bg-[#2b2118]/35 text-[#e8dcc0] hover:text-[#f4ecd8] shadow-none hover:shadow-md hover:shadow-black/15 border border-transparent hover:border-[#8b6914]/25 cursor-pointer transition-all duration-200 font-medium drop-shadow-sm',
		},
		link: {
			light: 'text-[#7c3aed] hover:text-[#6d28d9] font-medium hover:bg-[#7c3aed]/10 hover:shadow-sm hover:shadow-[#7c3aed]/10 cursor-pointer transition-all duration-200 drop-shadow-sm',
			dark: 'text-[#a78bfa] hover:text-[#c4b5fd] font-medium hover:bg-[#a78bfa]/10 hover:shadow-sm hover:shadow-[#a78bfa]/10 cursor-pointer transition-all duration-200 drop-shadow-sm',
		},
		accent: {
			light: 'backdrop-blur-xl bg-gradient-to-r from-[#d4af37]/10 to-[#7c3aed]/10 hover:from-[#d4af37]/20 hover:to-[#7c3aed]/20 text-[#6d28d9] hover:text-[#5b21b6] font-semibold shadow-lg shadow-[#7c3aed]/10 hover:shadow-xl hover:shadow-[#7c3aed]/20 border border-[#7c3aed]/20 hover:border-[#7c3aed]/40 cursor-pointer transition-all duration-200 drop-shadow-sm',
			dark: 'backdrop-blur-xl bg-gradient-to-r from-[#d4af37]/15 to-[#a78bfa]/15 hover:from-[#d4af37]/25 hover:to-[#a78bfa]/25 text-[#a78bfa] hover:text-[#c4b5fd] font-semibold shadow-lg shadow-[#a78bfa]/10 hover:shadow-xl hover:shadow-[#a78bfa]/20 border border-[#a78bfa]/20 hover:border-[#a78bfa]/40 cursor-pointer transition-all duration-200 drop-shadow-sm',
		},
		disabled: {
			light: 'backdrop-blur-xl bg-[#d9cba8]/60 text-[#8b7355] font-medium cursor-not-allowed opacity-60 shadow-sm border border-[#b8a373]/50',
			dark: 'backdrop-blur-xl bg-[#3a2f24]/60 text-[#8b7d63] font-medium cursor-not-allowed opacity-60 shadow-sm border border-[#4a3d2c]/50',
		},
	},
} as const;
